import { Link, useRouterState } from '@tanstack/react-router';
import {
  BookOpen,
  Terminal,
  Home,
  LogOut,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { useBillingStatus } from '@/features/account/api/use-billing';
import { SidebarPlan } from '@/features/account/components/sidebar-plan';
import { useAuth } from '@/features/auth/use-auth';
import { useSignOutAction } from '@/features/auth/use-sign-out-action';
import { MioRail } from '@/features/mio/components/mio-rail';
import { useMio } from '@/features/mio/use-mio';

import { cn } from '../lib/cn';
import { Button } from '../ui/button';
import { Logo, LogoMark } from '../ui/logo';
import { Spinner } from '../ui/spinner';
import { ThemeToggle } from '../ui/theme-toggle';
import { Tooltip } from '../ui/tooltip';
import { UsageBalance } from '../usage/usage-balance';
import { UsageContext } from '../usage/usage-context';

import { SidebarPosition } from './sidebar-position';

import type { LucideIcon } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';

interface NavItem {
  to: '/' | '/studio' | '/field';
  label: string;
  icon: LucideIcon;
}
const NAV: NavItem[] = [
  { to: '/', label: 'Overview', icon: Home },
  { to: '/studio', label: 'Studio', icon: BookOpen },
  { to: '/field', label: 'Workbench', icon: Terminal },
];

function Navigation({
  mobile = false,
  collapsed = false,
}: {
  mobile?: boolean;
  collapsed?: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <nav
      className={cn(
        'app-navigation',
        mobile ? 'app-shell-mobile-navigation grid grid-cols-3 gap-1 px-3 pb-3' : 'space-y-1',
      )}
      aria-label={mobile ? 'Mobile navigation' : 'Primary navigation'}
    >
      {NAV.map(({ to, label, icon: Icon }) => {
        const active =
          to === '/'
            ? pathname === '/'
            : pathname.startsWith(to) || (to === '/studio' && pathname.startsWith('/workshop'));
        const className = cn(
          'flex items-center rounded-md font-normal transition-colors',
          mobile
            ? 'h-12 flex-col justify-center gap-1 text-[12px]'
            : 'min-h-10 gap-3 px-3 py-2 text-sm',
          collapsed && !mobile ? 'justify-center px-0' : '',
          active ? 'bg-surface text-fg' : 'text-fg-muted hover:bg-surface hover:text-fg',
        );
        const content = (
          <>
            <Icon className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
            <span className={collapsed && !mobile ? 'sr-only' : ''}>{label}</span>
          </>
        );
        return (
          <Tooltip key={to} label={label} disabled={!collapsed || mobile}>
            <Link
              to={to}
              aria-current={active ? 'page' : undefined}
              title={collapsed ? label : undefined}
              className={className}
            >
              {content}
            </Link>
          </Tooltip>
        );
      })}
    </nav>
  );
}

function readCollapsed() {
  try {
    const saved = localStorage.getItem('mio.sidebar-collapsed');
    return saved === 'true' ? true : saved === 'false' ? false : null;
  } catch {
    return null;
  }
}

export function AppShell({
  title,
  subtitle,
  children,
  frame = 'page',
  workspace = false,
  contentId = 'main-content',
  visible = true,
  compact = false,
  focusMode = false,
}: {
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
  /** 'page' scrolls the content column with padding; 'full' hands the page its own scroll regions. */
  frame?: 'page' | 'full';
  /** Wider padded surface for the Workshop brief and IDE. */
  workspace?: boolean;
  contentId?: string;
  /** Keep hidden editor layouts mounted without a second active mentor panel. */
  visible?: boolean;
  /** Let a workbench own its header and default to the compact application rail. */
  compact?: boolean;
  /** Let a focused workshop use the viewport without unmounting its editor. */
  focusMode?: boolean;
}) {
  const { user, signOut } = useAuth();
  const {
    run: handleSignOut,
    force: forceSignOut,
    canForce: canForceSignOut,
    busy: signingOut,
    error: signOutError,
    workshopActivityId,
  } = useSignOutAction(signOut);
  const billing = useBillingStatus(visible);
  const mio = useMio();
  const [workspaceNavigationExpanded, setWorkspaceNavigationExpanded] = useState(false);
  const [sidebarPreference, setSidebarPreference] = useState(readCollapsed);
  const [narrowWorkspace, setNarrowWorkspace] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1279px)').matches,
  );
  const compactWorkspace = workspace && frame === 'full';
  const collapsed = compactWorkspace
    ? !workspaceNavigationExpanded
    : (sidebarPreference ?? (compact || (frame === 'full' && narrowWorkspace)));
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1279px)');
    const update = () => setNarrowWorkspace(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const name =
    typeof user?.user_metadata['display_name'] === 'string'
      ? user.user_metadata['display_name']
      : (user?.email?.split('@')[0] ?? 'Your account');
  /** A click on the rail itself, not on a link or control inside it, opens the sidebar. */
  function expandFromRail(event: MouseEvent<HTMLElement>) {
    if (!(event.target instanceof Element) || event.target.closest('a, button, input, select'))
      return;
    toggleSidebar();
  }
  function toggleSidebar() {
    if (compactWorkspace) {
      setWorkspaceNavigationExpanded((value) => !value);
      return;
    }
    const next = !collapsed;
    setSidebarPreference(next);
    try {
      localStorage.setItem('mio.sidebar-collapsed', String(next));
    } catch {
      /* Keep the preference for this page when storage is unavailable. */
    }
  }
  return (
    <div
      className={cn(
        'app-shell bg-sidebar relative h-dvh overflow-clip overscroll-none lg:flex',
        compact && 'app-shell-compact',
      )}
    >
      <a
        href={`#${contentId}`}
        className="bg-primary text-primary-fg fixed left-4 top-4 z-50 -translate-y-24 rounded-md px-4 py-3 text-sm focus:translate-y-0"
      >
        Skip to content
      </a>
      {/* The collapsed rail promises, through its cursor, that it opens to the right. */}
      <aside
        className={cn(
          'group/sidebar app-sidebar hidden h-full shrink-0 flex-col overflow-y-auto overscroll-contain lg:flex',
          focusMode && 'lg:hidden',
        )}
        data-collapsed={collapsed}
        onClick={collapsed ? expandFromRail : undefined}
      >
        <div className="app-sidebar-header">
          {collapsed ? (
            <Tooltip label="Expand sidebar">
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label="Expand sidebar"
                aria-expanded={false}
                className="group/brand hover:bg-surface focus-visible:ring-ring relative flex size-10 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2"
              >
                {/* Hovering anywhere on the rail turns the mark into the open-sidebar control. */}
                <LogoMark
                  size={32}
                  decorative
                  className="transition-opacity duration-150 group-hover/sidebar:opacity-0 group-focus-visible/brand:opacity-0"
                />
                <PanelLeftOpen
                  className="absolute size-5 opacity-0 transition-opacity duration-150 group-hover/sidebar:opacity-100 group-focus-visible/brand:opacity-100"
                  aria-hidden="true"
                />
              </button>
            </Tooltip>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <LogoMark size={28} decorative />
                <Logo height={22} />
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                type="button"
                aria-label="Collapse sidebar"
                aria-expanded
                className="panel-icon-control"
              >
                <PanelLeftClose className="size-[18px]" strokeWidth={1.7} />
              </Button>
            </>
          )}
        </div>
        <Navigation collapsed={collapsed} />
        <SidebarPosition collapsed={collapsed} />
        <div className="min-h-10 flex-1" />
        <Tooltip label="Mio · Your learning mentor (⌘J)" disabled={!collapsed}>
          <button
            type="button"
            onClick={mio.toggle}
            aria-pressed={mio.open}
            className={cn(
              'hover:bg-surface mx-3 flex min-h-10 items-center gap-2.5 rounded-md py-2 text-left transition-colors',
              collapsed ? 'mb-1 justify-center' : 'mb-3 px-3',
            )}
            aria-label="Mio"
          >
            <MessageSquare className="text-mio size-[18px] shrink-0" aria-hidden="true" />
            {!collapsed ? (
              <span className="min-w-0">
                <span className="block text-[13px] leading-5">Mio</span>
                <span className="text-fg-muted block text-xs leading-4">
                  {mio.open ? 'Your learning mentor' : 'Ask a question · ⌘J'}
                </span>
              </span>
            ) : null}
          </button>
        </Tooltip>
        <SidebarPlan status={billing.data} collapsed={collapsed} />
        <div
          className={cn(
            'flex min-h-10 items-center',
            collapsed ? 'mb-3 flex-col gap-1' : 'border-line mx-3 mb-3 gap-2 border-t px-3 pt-3',
          )}
        >
          {!collapsed ? (
            <div className="min-w-0 flex-1">
              <Link
                to="/account"
                className="hover:text-fg-muted block truncate text-sm transition-colors"
                aria-label="Account"
                title="Account"
              >
                {name}
              </Link>
            </div>
          ) : (
            <Tooltip label="Account">
              <Link
                to="/account"
                aria-label="Account"
                className="hover:bg-surface flex size-10 items-center justify-center rounded-md"
              >
                <UserRound className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
              </Link>
            </Tooltip>
          )}
          <ThemeToggle className={cn(collapsed && 'size-10')} />
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
            aria-label="Sign out"
            title="Sign out"
            className={cn(collapsed && 'size-10')}
          >
            <>{signingOut ? <Spinner /> : <LogOut className="size-4" />}</>
          </Button>
        </div>
      </aside>
      <div
        className={cn(
          'app-main-frame bg-canvas flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-clip',
          !focusMode && 'lg:border-line lg:rounded-lg lg:border',
        )}
      >
        <header
          className={cn(
            'app-shell-header app-content-header border-line shrink-0 border-b px-5 lg:px-8',
            compact && 'lg:hidden',
            (focusMode || compactWorkspace) && 'hidden',
          )}
        >
          <div className="app-shell-brandbar flex h-[52px] items-center gap-4">
            <div className="flex items-center gap-2 lg:hidden">
              <LogoMark size={28} decorative />
              <Logo height={21} />
            </div>
            <span className="text-fg-muted hidden text-sm lg:block">
              {title === 'Home' ? 'Overview' : title}
            </span>
            <div className="ml-auto flex items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={mio.toggle}
                className="text-fg text-sm font-medium"
                aria-label="Open Mio"
              >
                Mio
              </button>
              <Link to="/account" aria-label="Account" className="text-fg-muted p-2">
                <UserRound className="size-4" aria-hidden="true" />
              </Link>
              <ThemeToggle />
              <button
                type="button"
                onClick={() => void handleSignOut()}
                disabled={signingOut}
                className="text-fg-muted p-2"
                aria-label="Sign out"
              >
                <>{signingOut ? <Spinner /> : <LogOut className="size-4" />}</>
              </button>
            </div>
          </div>
          <div className="-mx-5 lg:hidden">
            <Navigation mobile />
          </div>
        </header>
        <UsageBalance
          usage={billing.data?.usage}
          compact
          className="border-line shrink-0 border-b px-4 py-2"
        />
        {signingOut ? (
          <p role="status" className="text-fg-muted px-5 py-2 text-sm">
            Signing out…
          </p>
        ) : null}
        {signOutError ? (
          <p role="alert" className="px-5 py-2 text-sm">
            {signOutError}
            {canForceSignOut ? (
              <>
                {' '}
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => void forceSignOut()}
                  disabled={signingOut}
                >
                  Sign out anyway
                </button>
              </>
            ) : null}
            {workshopActivityId !== undefined ? (
              <>
                {' '}
                {workshopActivityId ? (
                  <Link
                    to="/workshop/$activityId"
                    params={{ activityId: workshopActivityId }}
                    className="underline underline-offset-2"
                  >
                    Open Workshop
                  </Link>
                ) : (
                  <Link to="/workshop" className="underline underline-offset-2">
                    Open Workshop
                  </Link>
                )}
              </>
            ) : null}
          </p>
        ) : null}
        <main
          id={contentId}
          tabIndex={-1}
          className={cn(
            'min-h-0 flex-1 outline-none',
            frame === 'page'
              ? cn(
                  'overflow-y-auto overscroll-contain px-5 pb-12 pt-6 sm:px-8',
                  workspace ? 'lg:px-5 lg:pt-5' : 'lg:px-12 lg:pt-8',
                )
              : 'flex overflow-hidden',
          )}
        >
          {subtitle ? <span className="sr-only">{subtitle}</span> : null}
          <UsageContext.Provider value={billing.data?.usage ?? null}>
            {children}
          </UsageContext.Provider>
        </main>
      </div>
      {visible ? <MioRail usage={billing.data?.usage} /> : null}
    </div>
  );
}
